import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAlbumContext } from '@/contexts/AlbumProvider';
import { useAuth } from '@/hooks/useAuth';

type InteractionSeedItem = {
  id: string;
  votes_count?: number | null;
  comments_count?: number | null;
};

type CountsMap = Record<string, number>;

interface AlbumInteractionsContextType {
  loading: boolean;
  registerMediaItems: (items: InteractionSeedItem[]) => void;
  refresh: () => Promise<void>;
  getVotesCount: (mediaId: string, fallback?: number) => number;
  getCommentsCount: (mediaId: string, fallback?: number) => number;
  hasCurrentUserVoted: (mediaId: string, fallback?: boolean) => boolean;
  hasCurrentUserCommented: (mediaId: string, fallback?: boolean) => boolean;
  applyOptimisticCommentDelta: (mediaId: string, delta: number) => void;
  applyOptimisticVoteState: (mediaId: string, voted: boolean, voteCount?: number) => void;
}

const AlbumInteractionsContext = createContext<AlbumInteractionsContextType | undefined>(undefined);

const EMPTY_SET = new Set<string>();

export const AlbumInteractionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { albumId } = useAlbumContext();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [trackedMediaIds, setTrackedMediaIds] = useState<Set<string>>(new Set());
  const [votesByMediaId, setVotesByMediaId] = useState<CountsMap>({});
  const [commentsByMediaId, setCommentsByMediaId] = useState<CountsMap>({});
  const [currentUserVotedIds, setCurrentUserVotedIds] = useState<Set<string>>(new Set());
  const [currentUserCommentedIds, setCurrentUserCommentedIds] = useState<Set<string>>(new Set());
  const pendingOwnVoteInsertRef = useRef<Map<string, number>>(new Map());
  const pendingOwnVoteDeleteRef = useRef<Map<string, number>>(new Map());
  const pendingOwnCommentInsertRef = useRef<Map<string, number>>(new Map());
  const pendingOwnCommentDeleteRef = useRef<Map<string, number>>(new Map());
  const PENDING_EVENT_TTL_MS = 8000;
  const incrementPending = useCallback((pendingMap: React.MutableRefObject<Map<string, number>>, mediaId: string) => {
    pendingMap.current.set(mediaId, (pendingMap.current.get(mediaId) || 0) + 1);
    window.setTimeout(() => {
      const current = pendingMap.current.get(mediaId) || 0;
      if (current <= 1) pendingMap.current.delete(mediaId);
      else pendingMap.current.set(mediaId, current - 1);
    }, PENDING_EVENT_TTL_MS);
  }, []);

  const trackedMediaIdsArray = useMemo(() => Array.from(trackedMediaIds), [trackedMediaIds]);
  const trackedMediaIdsKey = useMemo(() => trackedMediaIdsArray.sort().join(','), [trackedMediaIdsArray]);

  useEffect(() => {
    setTrackedMediaIds(new Set());
    setVotesByMediaId({});
    setCommentsByMediaId({});
    setCurrentUserVotedIds(new Set());
    setCurrentUserCommentedIds(new Set());
    pendingOwnVoteInsertRef.current.clear();
    pendingOwnVoteDeleteRef.current.clear();
    pendingOwnCommentInsertRef.current.clear();
    pendingOwnCommentDeleteRef.current.clear();
  }, [albumId]);

  const registerMediaItems = useCallback((items: InteractionSeedItem[]) => {
    if (!items || items.length === 0) return;

    setTrackedMediaIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      items.forEach((item) => {
        if (item.id && !next.has(item.id)) {
          next.add(item.id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    setVotesByMediaId((prev) => {
      let changed = false;
      const next = { ...prev };
      items.forEach((item) => {
        if (!item.id) return;
        if (next[item.id] === undefined) {
          next[item.id] = item.votes_count ?? 0;
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    setCommentsByMediaId((prev) => {
      let changed = false;
      const next = { ...prev };
      items.forEach((item) => {
        if (!item.id) return;
        if (next[item.id] === undefined) {
          next[item.id] = item.comments_count ?? 0;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!albumId || trackedMediaIdsArray.length === 0) return;

    setLoading(true);
    try {
      const mediaIds = trackedMediaIdsArray;
      const voteCounts: CountsMap = {};
      const commentCounts: CountsMap = {};
      mediaIds.forEach((id) => {
        voteCounts[id] = 0;
        commentCounts[id] = 0;
      });

      const chunkSize = 200;
      for (let i = 0; i < mediaIds.length; i += chunkSize) {
        const chunk = mediaIds.slice(i, i + chunkSize);

        const [{ data: votesData, error: votesError }, { data: commentsData, error: commentsError }] = await Promise.all([
          supabase.from('media_votes').select('media_id').in('media_id', chunk),
          supabase.from('comments').select('media_id').in('media_id', chunk),
        ]);

        if (votesError) throw votesError;
        if (commentsError) throw commentsError;

        (votesData || []).forEach((row) => {
          if (!row.media_id) return;
          voteCounts[row.media_id] = (voteCounts[row.media_id] || 0) + 1;
        });
        (commentsData || []).forEach((row) => {
          if (!row.media_id) return;
          commentCounts[row.media_id] = (commentCounts[row.media_id] || 0) + 1;
        });
      }

      setVotesByMediaId(voteCounts);
      setCommentsByMediaId(commentCounts);

      if (user?.id) {
        const voted = new Set<string>();
        const commented = new Set<string>();
        const chunkSize = 200;
        for (let i = 0; i < mediaIds.length; i += chunkSize) {
          const chunk = mediaIds.slice(i, i + chunkSize);
          const [{ data: myVotes, error: myVotesError }, { data: myComments, error: myCommentsError }] = await Promise.all([
            supabase.from('media_votes').select('media_id').eq('user_id', user.id).in('media_id', chunk),
            supabase.from('comments').select('media_id').eq('user_id', user.id).in('media_id', chunk),
          ]);

          if (myVotesError) throw myVotesError;
          if (myCommentsError) throw myCommentsError;

          (myVotes || []).forEach((row) => {
            if (row.media_id) voted.add(row.media_id);
          });
          (myComments || []).forEach((row) => {
            if (row.media_id) commented.add(row.media_id);
          });
        }

        setCurrentUserVotedIds(voted);
        setCurrentUserCommentedIds(commented);
      } else {
        setCurrentUserVotedIds(new Set());
        setCurrentUserCommentedIds(new Set());
      }
    } catch (error) {
      console.error('Error refreshing album interactions:', error);
    } finally {
      setLoading(false);
    }
  }, [albumId, trackedMediaIdsArray, user?.id]);

  const recomputeUserCommentedMedia = useCallback(async (mediaId: string) => {
    if (!user?.id || !mediaId) return;
    const { count } = await supabase
      .from('comments')
      .select('id', { count: 'exact', head: true })
      .eq('media_id', mediaId)
      .eq('user_id', user.id);

    setCurrentUserCommentedIds((prev) => {
      const next = new Set(prev);
      if ((count || 0) > 0) next.add(mediaId);
      else next.delete(mediaId);
      return next;
    });
  }, [user?.id]);

  useEffect(() => {
    if (!albumId || trackedMediaIdsArray.length === 0) return;
    void refresh();
  }, [albumId, trackedMediaIdsKey, refresh]);

  useEffect(() => {
    if (!albumId) return;

    const trackedSet = new Set(trackedMediaIdsArray);
    const channel = supabase
      .channel(`album-interactions-${albumId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'media_votes' },
        (payload: { eventType: 'INSERT' | 'DELETE' | 'UPDATE'; old?: { media_id?: string; user_id?: string }; new?: { media_id?: string; user_id?: string } }) => {
          if (payload.eventType !== 'INSERT' && payload.eventType !== 'DELETE') return;
          const row = payload.eventType === 'DELETE' ? payload.old : payload.new;
          const mediaId = row?.media_id;
          const actorUserId = row?.user_id;
          if (!mediaId || !trackedSet.has(mediaId)) return;

          {
            const pendingMap = payload.eventType === 'INSERT' ? pendingOwnVoteInsertRef.current : pendingOwnVoteDeleteRef.current;
            const pending = pendingMap.get(mediaId) || 0;
            if (pending > 0) {
              if (pending === 1) pendingMap.delete(mediaId);
              else pendingMap.set(mediaId, pending - 1);
              return;
            }
          }

          const delta = payload.eventType === 'INSERT' ? 1 : -1;
          setVotesByMediaId((prev) => ({ ...prev, [mediaId]: Math.max(0, (prev[mediaId] || 0) + delta) }));

          if (user?.id && actorUserId === user.id) {
            setCurrentUserVotedIds((prev) => {
              const next = new Set(prev);
              if (delta > 0) next.add(mediaId);
              else next.delete(mediaId);
              return next;
            });
          }
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'comments' },
        (payload: { eventType: 'INSERT' | 'DELETE' | 'UPDATE'; old?: { media_id?: string; user_id?: string }; new?: { media_id?: string; user_id?: string } }) => {
          if (payload.eventType !== 'INSERT' && payload.eventType !== 'DELETE') return;
          const row = payload.eventType === 'DELETE' ? payload.old : payload.new;
          const mediaId = row?.media_id;
          const actorUserId = row?.user_id;
          if (!mediaId || !trackedSet.has(mediaId)) return;

          {
            const pendingMap = payload.eventType === 'INSERT' ? pendingOwnCommentInsertRef.current : pendingOwnCommentDeleteRef.current;
            const pending = pendingMap.get(mediaId) || 0;
            if (pending > 0) {
              if (pending === 1) pendingMap.delete(mediaId);
              else pendingMap.set(mediaId, pending - 1);
              return;
            }
          }

          const delta = payload.eventType === 'INSERT' ? 1 : -1;
          setCommentsByMediaId((prev) => ({ ...prev, [mediaId]: Math.max(0, (prev[mediaId] || 0) + delta) }));

          if (user?.id && actorUserId === user.id) {
            if (delta > 0) {
              setCurrentUserCommentedIds((prev) => new Set(prev).add(mediaId));
            } else {
              void recomputeUserCommentedMedia(mediaId);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [albumId, trackedMediaIdsKey, trackedMediaIdsArray, user?.id, recomputeUserCommentedMedia]);

  const getVotesCount = useCallback(
    (mediaId: string, fallback = 0) => votesByMediaId[mediaId] ?? fallback,
    [votesByMediaId],
  );

  const getCommentsCount = useCallback(
    (mediaId: string, fallback = 0) => commentsByMediaId[mediaId] ?? fallback,
    [commentsByMediaId],
  );

  const hasCurrentUserVoted = useCallback(
    (mediaId: string, fallback = false) => (currentUserVotedIds || EMPTY_SET).has(mediaId) || fallback,
    [currentUserVotedIds],
  );

  const hasCurrentUserCommented = useCallback(
    (mediaId: string, fallback = false) => (currentUserCommentedIds || EMPTY_SET).has(mediaId) || fallback,
    [currentUserCommentedIds],
  );

  const applyOptimisticCommentDelta = useCallback((mediaId: string, delta: number) => {
    if (!mediaId) return;
    setCommentsByMediaId((prev) => ({ ...prev, [mediaId]: Math.max(0, (prev[mediaId] || 0) + delta) }));
    if (user?.id) {
      if (delta > 0) incrementPending(pendingOwnCommentInsertRef, mediaId);
      else if (delta < 0) incrementPending(pendingOwnCommentDeleteRef, mediaId);
      if (delta > 0) {
        setCurrentUserCommentedIds((prev) => new Set(prev).add(mediaId));
      } else {
        void recomputeUserCommentedMedia(mediaId);
      }
    }
  }, [user?.id, recomputeUserCommentedMedia, incrementPending]);

  const applyOptimisticVoteState = useCallback((mediaId: string, voted: boolean, voteCount?: number) => {
    if (!mediaId) return;
    if (user?.id) {
      if (voted) incrementPending(pendingOwnVoteInsertRef, mediaId);
      else incrementPending(pendingOwnVoteDeleteRef, mediaId);
    }
    if (voteCount !== undefined) {
      setVotesByMediaId((prev) => ({ ...prev, [mediaId]: Math.max(0, voteCount) }));
    } else {
      setVotesByMediaId((prev) => ({ ...prev, [mediaId]: Math.max(0, (prev[mediaId] || 0) + (voted ? 1 : -1)) }));
    }

    if (user?.id) {
      setCurrentUserVotedIds((prev) => {
        const next = new Set(prev);
        if (voted) next.add(mediaId);
        else next.delete(mediaId);
        return next;
      });
    }
  }, [user?.id, incrementPending]);

  const value = useMemo<AlbumInteractionsContextType>(() => ({
    loading,
    registerMediaItems,
    refresh,
    getVotesCount,
    getCommentsCount,
    hasCurrentUserVoted,
    hasCurrentUserCommented,
    applyOptimisticCommentDelta,
    applyOptimisticVoteState,
  }), [
    loading,
    registerMediaItems,
    refresh,
    getVotesCount,
    getCommentsCount,
    hasCurrentUserVoted,
    hasCurrentUserCommented,
    applyOptimisticCommentDelta,
    applyOptimisticVoteState,
  ]);

  return (
    <AlbumInteractionsContext.Provider value={value}>
      {children}
    </AlbumInteractionsContext.Provider>
  );
};

export const useAlbumInteractions = () => {
  const context = useContext(AlbumInteractionsContext);
  if (!context) {
    throw new Error('useAlbumInteractions must be used within AlbumInteractionsProvider');
  }
  return context;
};
